import type {
	ReservedOrUserEventNames,
	ReservedOrUserListener,
} from '@socket.io/component-emitter';
import { useEffect } from 'react';
import type { SocketReservedEvents } from 'socket.io-client';

import type { ServerToClientEvents } from '@adflow/shared/util-websockets';

import { useWebsockets } from './use-websockets';

type EventNames = ReservedOrUserEventNames<
	SocketReservedEvents,
	ServerToClientEvents
>;

type EventListener<Ev extends EventNames> = ReservedOrUserListener<
	SocketReservedEvents,
	ServerToClientEvents,
	Ev
>;

/**
 * Subscribe to websocket events.
 *
 * This function takes the same arguments as `ClientSideSocket.on`.
 * @param ev - Name of the event
 * @param listener - Callback function
 */
export function useWebsocketListener<Ev extends EventNames>(
	ev: Ev,
	listener: EventListener<Ev>
) {
	const client = useWebsockets();

	// Using listener as a dependency without useCallback for this effect
	// should be fine; Adding and removing listeners should be very fast.
	useEffect(() => {
		client?.on<Ev>(ev, listener);
		return () => {
			client?.off<Ev>(ev, listener);
		};
	}, [client, ev, listener]);
}
